import { CapaActionStatus } from '@qualicloud/contracts';
import { useEffect, useId, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  Field,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  Textarea,
} from '@/design-system';
import type { CapaActionView } from '@/lib/api/sgq.types';
import { CapaActionStatusBadge, CapaDueBadge } from './sgq-badges';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ENCERRAMENTO DA NÃO-CONFORMIDADE — a tela que diz POR QUÊ não dá
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * No legado a NC era encerrada pela avaliação do gestor, num `UPDATE` que
 * gravava `status_nc = 'F'` junto com a procedência
 * (`model/qualidade/qualidade.php:32-37`). A regra que exigia plano de ação
 * completo — a C16 — existia em `finalizarNc`, e aquele endpoint NÃO TINHA
 * CHAMADOR (BUG-NC-04). Ou seja: a NC nascia e morria sem CAPA nenhum, e não
 * havia tela em lugar algum dizendo que faltava alguma coisa.
 *
 * Este diálogo é o oposto. A invariante continua sendo do SERVIDOR
 * (`assertCapaIsComplete`, dentro da transação do ato assinado); o que a tela
 * faz é exibir `closureBlockers` item a item e DESABILITAR a confirmação
 * enquanto houver pendência, para que o usuário não gaste uma assinatura num
 * ato que vai ser recusado.
 *
 * ACESSIBILIDADE: o bloqueio nunca é só o botão apagado. O motivo aparece num
 * `role="alert"` e o botão desabilitado carrega o motivo no próprio nome
 * acessível, porque elemento desabilitado não recebe foco e um `title` nunca
 * seria lido.
 */

export interface NcClosureDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Pendências apuradas pelo servidor. Vazio = pode encerrar. */
  readonly blockers: readonly string[];
  /** Ações do caso CAPA, para nomear o que exatamente está pendente. */
  readonly actions: readonly CapaActionView[];
  readonly onConfirm: (closingStatement: string) => void;
}

export function NcClosureDialog({
  open,
  onOpenChange,
  blockers,
  actions,
  onConfirm,
}: NcClosureDialogProps) {
  const blockingActionsId = useId();
  const [statement, setStatement] = useState('');

  useEffect(() => {
    if (open) setStatement('');
  }, [open]);

  const blocked = blockers.length > 0;
  const missingStatement = statement.trim().length === 0;
  const disabled = blocked || missingStatement;

  const pending = actions.filter(
    (action) =>
      action.status !== CapaActionStatus.EFFECTIVE &&
      action.status !== CapaActionStatus.CANCELLED,
  );
  const rejected = actions.filter((action) => action.effectivenessApproved === false);

  const disabledReason = blocked
    ? 'Encerramento bloqueado: há ação pendente ou eficácia reprovada.'
    : 'Registre a conclusão do tratamento para prosseguir.';

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Encerrar a não-conformidade"
      description="O encerramento é ato assinado e definitivo. Registre a conclusão do tratamento: é o texto que vai para o relatório consolidado e para a análise crítica pela direção."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={disabled}
            aria-label={
              disabled
                ? `Continuar para a assinatura — indisponível. ${disabledReason}`
                : undefined
            }
            onClick={() => onConfirm(statement.trim())}
          >
            Continuar para a assinatura
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {blocked ? (
          <div
            role="alert"
            className="rounded-md border border-warning bg-warning-subtle px-3 py-3 text-sm text-warning-content"
          >
            <p className="flex items-center gap-2 font-semibold">
              <IconAlertTriangle size={16} aria-hidden="true" />
              O encerramento está bloqueado
            </p>
            <p className="mt-1">
              ISO 13485 §8.5.2 exige verificar que a ação corretiva foi eficaz antes de dar o
              assunto por resolvido. Enquanto os pontos abaixo existirem, o servidor recusa o
              encerramento.
            </p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {blockers.map((blocker) => (
                <li key={blocker} className="flex items-start gap-2">
                  <IconAlertOctagon size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                  <span>{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md border border-success bg-success-subtle px-3 py-2.5 text-sm text-success-content"
          >
            <IconCheckCircle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Todo o plano de ação está concluído e com eficácia aprovada. A não-conformidade pode
              ser encerrada.
            </span>
          </p>
        )}

        {blocked && (pending.length > 0 || rejected.length > 0) ? (
          <div>
            <h3
              id={blockingActionsId}
              className="text-xs font-semibold uppercase tracking-wide text-content-subtle"
            >
              Ações que impedem o encerramento
            </h3>
            {/* A lista se apoia no próprio título em vez de repetir o texto num
                `aria-label`: com os dois, o leitor de tela anuncia o mesmo
                nome duas vezes ao entrar na lista. */}
            <ul aria-labelledby={blockingActionsId} className="mt-2 flex flex-col gap-1.5">
              {[...rejected, ...pending.filter((action) => !rejected.includes(action))].map(
                (action) => (
                  <li
                    key={action.id}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium text-content">
                      {action.title}
                    </span>
                    <CapaActionStatusBadge status={action.status} />
                    <CapaDueBadge
                      variant="chip"
                      state={action.dueState}
                      status={action.status}
                      dueDate={action.plan.when}
                    />
                    {action.effectivenessApproved === false ? (
                      <Badge tone="danger" size="sm">
                        Eficácia reprovada
                      </Badge>
                    ) : null}
                  </li>
                ),
              )}
            </ul>
            <p className="mt-2 text-xs text-content-subtle">
              A lista com links para cada ação está na tela da não-conformidade, no aviso acima das
              abas.
            </p>
          </div>
        ) : null}

        <Field
          label="Conclusão do tratamento"
          required
          description="O que foi feito, o que mudou e como se sabe que o problema não volta. Este texto é congelado no ato e não pode ser alterado depois."
        >
          <Textarea
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            rows={6}
            maxLength={4000}
            showCounter
            disabled={blocked}
          />
        </Field>
      </div>
    </Dialog>
  );
}
